import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-scroll";

function Hero({ className, site_call, next_section }) {
  const { t } = useTranslation();

  useEffect(() => {
    // Preload images
    const images = [
      "/images/hero/homepage.png",
      "/images/hero/homepage_mobile.png",
      "/images/hero/taolu.png",
      "/images/hero/taolu_mobile.png",
      "/images/hero/chin_woo.png",
      "/images/hero/chin_woo_mobile.png",
      "/images/hero/partners.png",
      "/images/hero/partners_mobile.png",
    ];

    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Bestimme Hintergrundbild basierend auf className
  const getBackgroundImage = () => {
    const basePath = process.env.PUBLIC_URL || "";
    
    switch (className) {
      case "hero-homepage":
        return {
          desktop: `${basePath}/images/hero/homepage.png`,
          mobile: `${basePath}/images/hero/homepage_mobile.png`,
        };
      case "hero-details":
        return {
          desktop: `${basePath}/images/hero/taolu.png`,
          mobile: `${basePath}/images/hero/taolu_mobile.png`,
        };
      case "hero-chinwoo-details":
        return {
          desktop: `${basePath}/images/hero/chin_woo.png`,
          mobile: `${basePath}/images/hero/chin_woo_mobile.png`,
        };
      case "hero-sponsors":
        return {
          desktop: `${basePath}/images/hero/partners.png`,
          mobile: `${basePath}/images/hero/partners_mobile.png`,
        };
      default:
        return {
          desktop: `${basePath}/images/hero/homepage.png`,
          mobile: `${basePath}/images/hero/homepage_mobile.png`,
        };
    }
  };

  const backgroundImages = getBackgroundImage();
  const isHomepage = className === "hero-homepage" || className === "hero-sponsors" || className === "hero-chinwoo-details";
  const isDetailsPage = className === "hero-details";

  return (
    <section
      className={`h-screen relative overflow-hidden bg-slate-900 dark:bg-slate-950 ${className} ${
        isHomepage ? "flex items-start justify-start" : 
        isDetailsPage ? "flex items-end justify-center" : 
        "flex items-center justify-center text-center"
      } px-5 py-16 md:px-6 md:py-20`}
      style={{
        backgroundImage: (isHomepage || className === "hero-chinwoo-details")
          ? `url(${backgroundImages.desktop})`
          : `linear-gradient(rgba(18, 18, 18, 0.2), rgba(18, 18, 18, 0.2)), url(${backgroundImages.desktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "scroll",
        minHeight: "100vh",
        position: "relative",
        zIndex: 1,
        isolation: "isolate",
      }}
    >
      {/* Subtle Gradient Overlay - nur für non-homepage und non-details */}
      {!isHomepage && !isDetailsPage && (
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
      )}

      {/* Mobile Gradient Overlay - nur für homepage mobile */}
      {isHomepage && className === "hero-homepage" && (
        <div className="absolute top-0 left-0 right-0 h-[30vh] bg-gradient-to-b from-black/50 via-black/20 to-transparent pointer-events-none md:hidden z-[5]"></div>
      )}
      
      {/* Mobile Gradient Overlay - für chinwoo-details mobile */}
      {className === "hero-chinwoo-details" && (
        <div className="absolute top-0 left-0 right-0 h-[30vh] bg-gradient-to-b from-black/50 via-black/20 to-transparent pointer-events-none md:hidden z-[5]"></div>
      )}

      {/* Bottom Border Line */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-80"></div>

      {/* Content */}
      {isHomepage ? (
        <>
          {/* Text Content - Mobile: oberes Drittel, Desktop: links oben */}
          <div className="max-w-2xl relative z-10 mt-12 md:mt-16 lg:mt-20 ml-4 md:ml-8 lg:ml-12 xl:ml-16 mobile-hero-text-container">
            {/* Label - Hidden on mobile */}
            {(() => {
              const label = t(`hero.${site_call}.label`, { defaultValue: null });
              return label && label !== `hero.${site_call}.label` ? (
                <p className="text-[0.625rem] md:text-[0.6875rem] uppercase tracking-[0.15em] text-slate-300/60 mb-4 md:mb-5 font-light hero-label-animation hidden md:block">
                  {label}
                </p>
              ) : null;
            })()}
            
            {/* Main Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-8 md:mb-10 text-white leading-tight drop-shadow-lg hero-title-animation whitespace-pre-line">
              {t(`hero.${site_call}.title`)}
            </h1>
            
            {/* Subline */}
            {(() => {
              const subline = t(`hero.${site_call}.subline`, { defaultValue: null });
              return subline && subline !== `hero.${site_call}.subline` ? (
                <p className="text-base md:text-lg lg:text-xl text-white/95 mb-6 md:mb-7 leading-relaxed hero-subline-animation font-medium">
                  {subline}
                </p>
              ) : null;
            })()}
            
            {/* Info Line - Hidden on mobile */}
            {(() => {
              const info = t(`hero.${site_call}.info`, { defaultValue: null });
              return info && info !== `hero.${site_call}.info` ? (
                <div className="mb-10 md:mb-12 hero-info-animation hidden md:block">
                  <div className="flex items-center justify-center mb-3 md:mb-4">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
                    <div className="mx-4">
                      <svg
                        className="w-4 h-4 text-white/50 dark:text-white/60"
                        viewBox="0 0 8 8"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polygon points="4,0 8,4 4,8 0,4" />
                      </svg>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
                  </div>
                  <p className="text-xs md:text-sm text-white/70 tracking-tight font-light uppercase">
                    {info}
                  </p>
                </div>
              ) : null;
            })()}
            
            {/* CTA - Desktop: innerhalb Text-Container */}
            <div className={`hero-cta-animation ${className === "hero-chinwoo-details" || className === "hero-sponsors" ? "block" : "hidden md:block"}`}>
              <Link
                to={next_section}
                smooth={true}
                duration={1500}
                className={`inline-flex items-center justify-center gap-2 ${className === "hero-chinwoo-details" ? "px-6 py-3 text-sm" : "px-8 py-4 text-lg"} bg-primary dark:bg-primary-light text-white font-semibold rounded-full border-2 border-primary dark:border-primary-light transition-all duration-300 hover:bg-primary/90 dark:hover:bg-primary-light/90 hover:-translate-y-1 hover:shadow-lg cursor-pointer`}
              >
                {(() => {
                  const cta = t(`hero.${site_call}.cta`, { defaultValue: null });
                  return cta && cta !== `hero.${site_call}.cta` ? cta : t("button.see_more");
                })()}
              </Link>
            </div>
          </div>
          
          {/* Mobile: Meta-Daten und CTA */}
          {className !== "hero-chinwoo-details" && className !== "hero-sponsors" && (
            <div className="mobile-hero-cta-container md:hidden">
            {/* Info Line - Mobile: über dem Button */}
            <div className="hero-info-animation mb-4">
              <div className="flex items-center justify-center mb-2">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
                <div className="mx-3">
                  <svg
                    className="w-3 h-3 text-white/50 dark:text-white/60"
                    viewBox="0 0 8 8"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polygon points="4,0 8,4 4,8 0,4" />
                  </svg>
                </div>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
              </div>
              <p className="text-[0.625rem] text-white/70 tracking-tight font-light uppercase text-center">
                {t(`hero.${site_call}.info`)}
              </p>
            </div>
            
            {/* CTA - Mobile */}
            <div className="hero-cta-animation">
              <Link
                to={next_section}
                smooth={true}
                duration={1500}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-semibold rounded-full border-2 border-primary dark:border-primary-light transition-all duration-300 hover:bg-primary/90 dark:hover:bg-primary-light/90 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
              >
                {(() => {
                  const cta = t(`hero.${site_call}.cta`, { defaultValue: null });
                  return cta && cta !== `hero.${site_call}.cta` ? cta : t("button.see_more");
                })()}
              </Link>
            </div>
          </div>
          )}
        </>
      ) : isDetailsPage ? (
        <>
          {/* Text Content - Links versetzt, unten mittig */}
          <div className={`max-w-2xl relative z-10 ${className === "hero-chinwoo-details" ? "mt-12 md:mt-16 lg:mt-20 mb-12 md:mb-16 lg:mb-20 ml-4 md:ml-8 lg:ml-12 xl:ml-16" : "mb-12 md:mb-16 lg:mb-20 ml-4 md:ml-8 lg:ml-12 xl:ml-16 md:-ml-8 lg:-ml-12 xl:-ml-16"} details-hero-text-container`}>
            {/* Label - Hidden on mobile */}
            {(() => {
              const label = t(`hero.${site_call}.label`, { defaultValue: null });
              return label && label !== `hero.${site_call}.label` ? (
                <p className="text-[0.625rem] md:text-[0.6875rem] uppercase tracking-[0.15em] text-slate-300/60 mb-4 md:mb-5 font-light hero-label-animation hidden md:block">
                  {label}
                </p>
              ) : null;
            })()}
            
            {/* Main Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-8 md:mb-10 text-white leading-tight drop-shadow-lg hero-title-animation whitespace-pre-line">
              {t(`hero.${site_call}.title`)}
            </h1>
            
            {/* Subline */}
            {(() => {
              const subline = t(`hero.${site_call}.subline`, { defaultValue: null });
              return subline && subline !== `hero.${site_call}.subline` ? (
                <p className="text-base md:text-lg lg:text-xl text-white/95 mb-6 md:mb-7 leading-relaxed hero-subline-animation font-medium">
                  {subline}
                </p>
              ) : null;
            })()}
            
            {/* Info Line - Hidden on mobile */}
            {(() => {
              const info = t(`hero.${site_call}.info`, { defaultValue: null });
              return info && info !== `hero.${site_call}.info` ? (
                <div className="mb-10 md:mb-12 hero-info-animation hidden md:block">
                  <div className="flex items-center justify-center mb-3 md:mb-4">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
                    <div className="mx-4">
                      <svg
                        className="w-4 h-4 text-white/50 dark:text-white/60"
                        viewBox="0 0 8 8"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polygon points="4,0 8,4 4,8 0,4" />
                      </svg>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
                  </div>
                  <p className="text-xs md:text-sm text-white/70 tracking-tight font-light uppercase">
                    {info}
                  </p>
                </div>
              ) : null;
            })()}
            
            {/* CTA - innerhalb Text-Container */}
            <div className="hero-cta-animation">
              <Link
                to={next_section}
                smooth={true}
                duration={1500}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-semibold rounded-full border-2 border-primary dark:border-primary-light transition-all duration-300 hover:bg-primary/90 dark:hover:bg-primary-light/90 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
              >
                {(() => {
                  const cta = t(`hero.${site_call}.cta`, { defaultValue: null });
                  return cta && cta !== `hero.${site_call}.cta` ? cta : t("button.see_more");
                })()}
              </Link>
            </div>
          </div>
          
          {/* Mobile: Meta-Daten und CTA - nur für hero-details, nicht für hero-chinwoo-details */}
          {className === "hero-details" && (
            <div className="mobile-hero-cta-container md:hidden">
              {/* Info Line - Mobile: über dem Button */}
              {(() => {
                const info = t(`hero.${site_call}.info`, { defaultValue: null });
                return info && info !== `hero.${site_call}.info` ? (
                  <div className="hero-info-animation mb-4">
                    <div className="flex items-center justify-center mb-2">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
                      <div className="mx-3">
                        <svg
                          className="w-3 h-3 text-white/50 dark:text-white/60"
                          viewBox="0 0 8 8"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <polygon points="4,0 8,4 4,8 0,4" />
                        </svg>
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-l from-transparent via-white/30 to-white/30 dark:from-transparent dark:via-white/40 dark:to-white/40"></div>
                    </div>
                    <p className="text-[0.625rem] text-white/70 tracking-tight font-light uppercase text-center">
                      {info}
                    </p>
                  </div>
                ) : null;
              })()}
              
              {/* CTA - Mobile */}
              <div className="hero-cta-animation">
                <Link
                  to={next_section}
                  smooth={true}
                  duration={1500}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-semibold rounded-full border-2 border-primary dark:border-primary-light transition-all duration-300 hover:bg-primary/90 dark:hover:bg-primary-light/90 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
                >
                  {(() => {
                    const cta = t(`hero.${site_call}.cta`, { defaultValue: null });
                    return cta && cta !== `hero.${site_call}.cta` ? cta : t("button.see_more");
                  })()}
                </Link>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 text-white drop-shadow-lg hero-title-animation">
            {t(`hero.${site_call}.title`)}
          </h2>
          {(() => {
            const subline = t(`hero.${site_call}.subline`, { defaultValue: null });
            return subline && subline !== `hero.${site_call}.subline` ? (
              <p className="text-xl md:text-2xl text-white/95 mb-5 leading-relaxed hero-subline-animation font-medium">
                {subline}
              </p>
            ) : null;
          })()}
          <p className="text-lg md:text-xl text-white/90 mb-10 leading-relaxed hero-description-animation">
            {t(`hero.${site_call}.description`)}
          </p>
          <Link
            to={next_section}
            smooth={true}
            duration={1500}
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary dark:bg-primary-light text-white text-lg font-semibold rounded-full border-2 border-primary dark:border-primary-light transition-all duration-300 hover:bg-transparent hover:text-primary dark:hover:text-primary-light hover:-translate-y-1 hover:shadow-lg hero-button-animation cursor-pointer"
          >
            {t("button.see_more")}
          </Link>
        </div>
      )}

      {/* Mobile Background Image Override and Animations */}
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .hero-label-animation {
          animation: fadeInDown 0.8s ease-out;
        }

        .hero-title-animation {
          animation: fadeInDown 1s ease-out 0.1s both;
        }

        .hero-subline-animation {
          animation: fadeInUp 1s ease-out 0.2s both;
        }

        .hero-info-animation {
          animation: fadeInUp 1s ease-out 0.3s both;
        }

        .hero-cta-animation {
          animation: fadeIn 1.2s ease-out 0.4s both;
        }

        .hero-description-animation {
          animation: fadeInUp 1s ease-out;
        }

        .hero-button-animation {
          animation: fadeIn 1.5s ease-out;
        }

        @media screen and (max-width: 768px) {
          section[class*="h-screen"] {
            background-image: ${isHomepage || isDetailsPage
              ? `url(${backgroundImages.mobile})` 
              : `linear-gradient(rgba(18, 18, 18, 0.2), rgba(18, 18, 18, 0.2)), url(${backgroundImages.mobile})`} !important;
            background-size: cover !important;
            background-position: ${isHomepage && className === "hero-chinwoo-details" ? '80% center' : isHomepage && className === "hero-sponsors" ? '80% center' : isHomepage ? 'right center' : isDetailsPage && className === "hero-chinwoo-details" ? 'right center' : isDetailsPage ? 'left center' : 'center'} !important;
            background-repeat: no-repeat !important;
            background-attachment: scroll !important;
            background-clip: padding-box !important;
          }
          ${isHomepage ? `
          /* Mobile Hero Layout - Text im oberen Drittel */
          section[class*="h-screen"].hero-homepage .mobile-hero-text-container {
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-top: 0 !important;
            padding: 4.5rem 1.25rem 1rem 1.25rem !important;
            text-align: left !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            height: 33.333vh !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: flex-start !important;
            z-index: 10 !important;
          }
          
          /* Mobile Hero Layout für Chin Woo und Sponsors - Text weiter oben links (wie Details Page) */
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container {
            max-width: 50% !important;
            margin-left: 1.25rem !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding: 0 !important;
            text-align: left !important;
            position: absolute !important;
            bottom: 16rem !important;
            top: auto !important;
            left: 0 !important;
            right: auto !important;
            height: auto !important;
            display: block !important;
            z-index: 10 !important;
          }
          
          /* Chin Woo Mobile: Label, Titel, Subline Styling */
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container p:first-of-type {
            font-size: 0.5rem !important;
            margin-bottom: 0.5rem !important;
            opacity: 0.7 !important;
            line-height: 1.4 !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container h1 {
            font-size: 1.5rem !important;
            line-height: 1.25 !important;
            margin-bottom: 0.75rem !important;
            font-weight: 700 !important;
            letter-spacing: -0.02em !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container p:nth-of-type(2) {
            font-size: 0.8125rem !important;
            margin-bottom: 1rem !important;
            line-height: 1.4 !important;
            opacity: 0.95 !important;
            font-weight: 500 !important;
          }
          
          /* Chin Woo und Sponsors Mobile: Info Line sichtbar */
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation {
            display: block !important;
            margin-bottom: 1rem !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation > div:first-child,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation > div:first-child {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin-bottom: 0.75rem !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:first-child,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:last-child,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:first-child,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:last-child {
            flex: 1 !important;
            height: 1px !important;
            background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)) !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:last-child,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:last-child {
            background: linear-gradient(to left, transparent, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)) !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2),
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2) {
            margin: 0 1rem !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2) svg,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2) svg {
            width: 0.75rem !important;
            height: 0.75rem !important;
            color: rgba(255, 255, 255, 0.5) !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-info-animation > p,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-info-animation > p {
            font-size: 0.625rem !important;
            text-align: left !important;
            color: rgba(255, 255, 255, 0.7) !important;
          }
          
          /* Chin Woo und Sponsors Mobile: CTA Button */
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-cta-animation,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-cta-animation {
            display: block !important;
            margin-top: 1rem !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container > div.hero-cta-animation a,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container > div.hero-cta-animation a {
            font-size: 0.875rem !important;
            line-height: 1.25rem !important;
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
            padding-top: 0.75rem !important;
            padding-bottom: 0.75rem !important;
            font-weight: 600 !important;
            min-height: auto !important;
            height: auto !important;
          }
          
          /* Chin Woo und Sponsors Mobile: CTA Container ausblenden */
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container {
            display: none !important;
          }
          
          /* Label - sehr klein und subtil */
          section[class*="h-screen"].hero-homepage .mobile-hero-text-container p:first-of-type {
            font-size: 0.5rem !important;
            margin-bottom: 0.5rem !important;
            opacity: 0.7 !important;
            line-height: 1.4 !important;
          }
          
          /* Hauptüberschrift - kompakt im oberen Drittel */
          section[class*="h-screen"].hero-homepage .mobile-hero-text-container h1 {
            font-size: 1.5rem !important;
            line-height: 1.25 !important;
            margin-bottom: 0.75rem !important;
            font-weight: 700 !important;
            letter-spacing: -0.02em !important;
          }
          
          /* Subline - kompakt */
          section[class*="h-screen"].hero-homepage .mobile-hero-text-container p:nth-of-type(2) {
            font-size: 0.8125rem !important;
            margin-bottom: 0 !important;
            line-height: 1.4 !important;
            opacity: 0.95 !important;
            font-weight: 500 !important;
          }
          
          /* Chin Woo und Sponsors Mobile: Label, Titel, Subline Styling */
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container p:first-of-type,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container p:first-of-type {
            font-size: 0.5rem !important;
            margin-bottom: 0.5rem !important;
            opacity: 0.7 !important;
            line-height: 1.4 !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container h1,
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container h1 {
            font-size: 1.5rem !important;
            line-height: 1.25 !important;
            margin-bottom: 0.75rem !important;
            font-weight: 700 !important;
            letter-spacing: -0.02em !important;
          }
          
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-text-container p:nth-of-type(2),
          section[class*="h-screen"].hero-sponsors .mobile-hero-text-container p:nth-of-type(2) {
            font-size: 0.8125rem !important;
            margin-bottom: 1rem !important;
            line-height: 1.4 !important;
            opacity: 0.95 !important;
            font-weight: 500 !important;
          }
          
          /* CTA Container - Meta-Daten und Button */
          section[class*="h-screen"].hero-homepage .mobile-hero-cta-container,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container {
            position: absolute !important;
            bottom: 3rem !important;
            left: 1.25rem !important;
            right: 1.25rem !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            z-index: 10 !important;
          }
          
          /* Meta-Daten über dem Button */
          section[class*="h-screen"].hero-homepage .mobile-hero-cta-container > div:first-child,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container > div:first-child,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container > div:first-child {
            margin-bottom: 1rem !important;
          }
          
          section[class*="h-screen"].hero-homepage .mobile-hero-cta-container > div:first-child > div:first-child,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container > div:first-child > div:first-child,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container > div:first-child > div:first-child {
            flex: 1 !important;
            height: 1px !important;
            margin-bottom: 0 !important;
          }
          
          section[class*="h-screen"].hero-homepage .mobile-hero-cta-container > div:first-child > div:last-child,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container > div:first-child > div:last-child,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container > div:first-child > div:last-child {
            flex: 1 !important;
            height: 1px !important;
            margin-bottom: 0 !important;
          }
          
          section[class*="h-screen"].hero-homepage .mobile-hero-cta-container > div:first-child > div:nth-child(2),
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container > div:first-child > div:nth-child(2),
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container > div:first-child > div:nth-child(2) {
            margin: 0 0.75rem !important;
          }
          
          section[class*="h-screen"].hero-homepage .mobile-hero-cta-container > div:first-child > p,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container > div:first-child > p,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container > div:first-child > p {
            font-size: 0.625rem !important;
          }
          
          /* Button - Primary-Style */
          section[class*="h-screen"].hero-homepage .mobile-hero-cta-container > div:last-child a,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container > div:last-child a,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container > div:last-child a {
            font-size: 0.875rem !important;
            padding: 0.75rem 1.5rem !important;
            font-weight: 600 !important;
          }
          
          /* Desktop: CTA wieder normal positioniert */
          @media screen and (min-width: 768px) {
            section[class*="h-screen"].hero-homepage .mobile-hero-cta-container,
            section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container,
            section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container {
              position: relative !important;
              bottom: auto !important;
              left: auto !important;
              right: auto !important;
              display: block !important;
              justify-content: flex-start !important;
            }
          }
          ` : ''}
          ${isDetailsPage ? `
          /* Mobile Hero Layout für Details Page - Text weiter unten links */
          section[class*="h-screen"].hero-details .details-hero-text-container,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container {
            max-width: 50% !important;
            margin-left: 1.25rem !important;
            margin-bottom: 0 !important;
            padding: 0 !important;
            text-align: left !important;
            position: absolute !important;
            bottom: 10rem !important;
            top: auto !important;
            transform: none !important;
            left: 0 !important;
            right: auto !important;
            z-index: 10 !important;
          }
          
          /* Mobile Hero Layout für Chin Woo Details - Text weiter oben */
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container {
            max-width: 50% !important;
            margin-left: 1.25rem !important;
            margin-bottom: 0 !important;
            padding: 0 !important;
            text-align: left !important;
            position: absolute !important;
            bottom: 25rem !important;
            top: auto !important;
            transform: none !important;
            left: 0 !important;
            right: auto !important;
            z-index: 10 !important;
          }
          
          /* Label - sehr klein und subtil */
          section[class*="h-screen"].hero-details .details-hero-text-container p:first-of-type,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container p:first-of-type,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container p:first-of-type {
            font-size: 0.5rem !important;
            margin-bottom: 0.5rem !important;
            opacity: 0.7 !important;
            line-height: 1.4 !important;
          }
          
          /* Hauptüberschrift - kompakt */
          section[class*="h-screen"].hero-details .details-hero-text-container h1,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container h1,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container h1 {
            font-size: 1.5rem !important;
            line-height: 1.25 !important;
            margin-bottom: 0.75rem !important;
            font-weight: 700 !important;
            letter-spacing: -0.02em !important;
          }
          
          /* Subline - kompakt */
          section[class*="h-screen"].hero-details .details-hero-text-container p:nth-of-type(2),
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container p:nth-of-type(2),
          section[class*="h-screen"].hero-sponsors .details-hero-text-container p:nth-of-type(2) {
            font-size: 0.8125rem !important;
            margin-bottom: 1rem !important;
            line-height: 1.4 !important;
            opacity: 0.95 !important;
            font-weight: 500 !important;
          }
          
          /* Info Line - wieder sichtbar auf mobile */
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation {
            display: block !important;
            margin-bottom: 1rem !important;
          }
          
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation > div:first-child,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation > div:first-child,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation > div:first-child {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin-bottom: 0.75rem !important;
          }
          
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:first-child,
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:last-child,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:first-child,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:last-child,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation > div:first-child > div:first-child,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation > div:first-child > div:last-child {
            flex: 1 !important;
            height: 1px !important;
            background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)) !important;
          }
          
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:last-child,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:last-child,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation > div:first-child > div:last-child {
            background: linear-gradient(to left, transparent, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)) !important;
          }
          
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2),
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2),
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2) {
            margin: 0 1rem !important;
          }
          
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2) svg,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2) svg,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation > div:first-child > div:nth-child(2) svg {
            width: 0.75rem !important;
            height: 0.75rem !important;
            color: rgba(255, 255, 255, 0.5) !important;
          }
          
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-info-animation > p,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-info-animation > p,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-info-animation > p {
            font-size: 0.625rem !important;
            text-align: left !important;
            color: rgba(255, 255, 255, 0.7) !important;
          }
          
          /* CTA Button - innerhalb Text-Container */
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-cta-animation,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-cta-animation,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-cta-animation {
            display: block !important;
            margin-top: 1rem !important;
          }
          
          section[class*="h-screen"].hero-details .details-hero-text-container > div.hero-cta-animation a,
          section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div.hero-cta-animation a,
          section[class*="h-screen"].hero-sponsors .details-hero-text-container > div.hero-cta-animation a {
            font-size: 0.875rem !important;
            padding: 0.75rem 1.5rem !important;
            font-weight: 600 !important;
          }
          
          /* Mobile CTA Container ausblenden */
          section[class*="h-screen"].hero-details .mobile-hero-cta-container,
          section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container,
          section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container {
            display: none !important;
          }
          
          /* Desktop: CTA wieder normal positioniert */
          @media screen and (min-width: 768px) {
            section[class*="h-screen"].hero-details .details-hero-text-container,
            section[class*="h-screen"].hero-sponsors .details-hero-text-container {
              position: relative !important;
              top: auto !important;
              transform: none !important;
              max-width: 2xl !important;
              margin-left: 0 !important;
              margin-bottom: 3rem !important;
            }
            
            section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container {
              position: relative !important;
              top: auto !important;
              transform: none !important;
              max-width: 2xl !important;
              margin-left: 0 !important;
              margin-top: 0 !important;
              margin-bottom: 3rem !important;
            }
            
            section[class*="h-screen"].hero-details .details-hero-text-container > div,
            section[class*="h-screen"].hero-chinwoo-details .details-hero-text-container > div,
            section[class*="h-screen"].hero-sponsors .details-hero-text-container > div {
              display: block !important;
            }
            
            section[class*="h-screen"].hero-details .mobile-hero-cta-container,
            section[class*="h-screen"].hero-chinwoo-details .mobile-hero-cta-container,
            section[class*="h-screen"].hero-sponsors .mobile-hero-cta-container {
              display: none !important;
            }
          }
          ` : ''}
        }
        
        /* Tablet: Bild von rechts, rechte Seite niemals abgeschnitten */
        @media screen and (min-width: 768px) and (max-width: 1024px) {
          ${isHomepage ? `
          section[class*="h-screen"].hero-homepage,
          section[class*="h-screen"].hero-chinwoo-details,
          section[class*="h-screen"].hero-sponsors {
            background-position: right center !important;
          }
          ` : ''}
        }
        
        @media screen and (max-width: 1024px) {
          section[class*="h-screen"] {
            background-attachment: scroll !important;
          }
        }
        
        /* Sicherstellen, dass das Hintergrundbild nur innerhalb der Hero-Section bleibt */
        section[class*="h-screen"]::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: transparent;
          z-index: 1;
        }
      `}</style>
    </section>
  );
}

export default Hero;
